import { useEffect, useRef } from 'react';
import './ReserverEnLigne.css'
import '@azoya-spa/ui/styles.css';
import { useBookingSettings } from '../../hooks/useBookingSettings'
import { useSiteContent } from '../../hooks/useSiteContent'
import practicalInfoFallback from '../../api/fallback/practicalInfo.json'
import { frenchNumberToTelHref } from '../../utils/phone'
import { PlanityWidgetLoading, PlanityWidgetUnavailable } from '../../components/planity/PlanityWidgetStatus'
import {
  giftCardOpened, phoneClicked, planityExternalLinkClicked, planityWidgetLoaded,
} from '../../analytics/events'

function ReserverEnLigne() {


  const widgetContainerRef = useRef(null);
  const widgetInjectedRef = useRef(false);
  const { booking, status: bookingStatus } = useBookingSettings();
  const info = useSiteContent('practical-info', practicalInfoFallback);

  /* Le module part au montage de la page, plus au clic : le choix entre
     « ici » et « chez Planity » a été supprimé, il n'y a donc plus de geste
     préalable derrière lequel différer le chargement. Ce que le différé
     protégeait était une visiteuse arrivée par erreur sur une page où
     personne n'arrive par erreur — le bouton « Réserver en ligne » est le
     seul appel à l'action de l'en-tête.

     Rien à demander au consentement : le module est classé strictement
     nécessaire (cf. src/consent/ConsentGate.jsx).

     bookingStatus !== 'ready' : cf. useBookingSettings — n'injecte que
     lorsque la vraie clé est confirmée reçue de l'API. Le conteneur affiche
     un indicateur de chargement (ou, en cas d'échec, un message
     d'indisponibilité) tant que ce n'est pas le cas. */
  useEffect(() => {
    if (bookingStatus !== 'ready' || widgetInjectedRef.current) return;
    const container = widgetContainerRef.current;
    if (!container) return;
    widgetInjectedRef.current = true;

    window.planity = {
      key: booking.planityWidgetKey,
      primaryColor: '#e86f51',
      options: {
        headerWidth: '100px'
      },
      appointmentContainer: container,
    };

    const script = document.createElement('script');
    script.src = "https://d2skjte8udjqxw.cloudfront.net/widget/production/polyfills.latest.js";
    script.async = true;
    const script2 = document.createElement('script');
    script2.src = "https://d2skjte8udjqxw.cloudfront.net/widget/production/app.latest.js";
    script2.async = true;
    // Sur le `onload`, pas sur l'injection : l'injection dit « on a essayé »,
    // le `onload` dit « le module s'est chargé ». C'est la seconde qui répond
    // à « combien ouvrent réellement le module ». Sur le SECOND script
    // seulement : le premier n'est que le polyfill, et les deux se chargent en
    // vrai navigateur — l'y poser aussi doublerait l'événement. Un re-rendu de
    // la page ne réinjecte rien (widgetInjectedRef ci-dessus) : il ne peut donc
    // pas produire un second `onload`.
    script2.onload = () => planityWidgetLoaded();

    container.appendChild(script);
    container.appendChild(script2);
  }, [bookingStatus, booking]);

  // Les horaires sont saisis librement au back-office, ligne par ligne : la
  // première est le résumé (« Ouvert 5/7 jours »). La maquette écrivait
  // « ouvert cinq jours sur sept — jeudi et dimanche fermé » en toutes
  // lettres ; le figer ici ferait mentir la page le jour où l'institut ouvre
  // le jeudi. Le détail complet reste au pied de page et sur l'écran
  // d'indisponibilité, qui affichent toutes les lignes.
  const resumeHoraires = info.openingLines?.[0]

  return (
    <div className="ReserverEnLigne" id='ReserverEnLigne'>

      <div className="rz-topline">
        <div className="rz-intro">
          <h1 className="rz-title">Réserver en ligne</h1>
          <p className="rz-subtitle">
            Choisissez votre soin, votre praticienne et votre créneau. Institut d’Auxerre
            {resumeHoraires ? ` — ${resumeHoraires}` : ''}.
          </p>
        </div>

        {/* Quatrième point d'appel du site pour `phone_clicked`, après le pied
            de page, l'impasse de recherche des Services et le repli Planity :
            l'omettre laisserait hors mesure le téléphone de la page qui porte
            la conversion. Comme les trois autres, il n'a pas d'emplacement —
            voir le commentaire de PlanityWidgetStatus.jsx. */}
        <a
          className="rz-phone"
          href={frenchNumberToTelHref(info.phone)}
          onClick={() => phoneClicked()}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#e86f51" strokeWidth="1.8" aria-hidden="true">
            <path d="M4 4h4l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 3 5a1 1 0 0 1 1-1z" />
          </svg>
          Une question ? {info.phone}
        </a>
      </div>

      {/* Le cadre annonce l'invité franchement : sous le liseré crème, tout
          appartient au tiers. La frontière est une ligne, pas un dégradé —
          le module Planity n'aura jamais notre typographie ni nos arrondis,
          et le cadre ne prétend pas le contraire. */}
      <section className="rz-frame" aria-label="Module de réservation Planity">
        <div className="rz-frame-head">
          <span className="rz-over">Réservation assurée par Planity</span>
          {/* La page Planity officielle n'est plus la moitié d'un choix à
              trancher : elle est une sortie secondaire permanente, visible
              sur les trois états du module. */}
          <a
            className="rz-planity-link"
            href={booking.planityPublicUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => planityExternalLinkClicked()}
          >
            <span className="rz-planity-link-text">Ouvrir sur planity.com</span>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#264653" strokeWidth="2" aria-hidden="true">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
            </svg>
            <span className="rz-hors-ecran">(nouvel onglet)</span>
          </a>
        </div>

        {/* Le rembourrage est ce que le cadre prend au module : 12 px sur
            bureau, zéro sous 700 px. La largeur appartient au tiers. */}
        <div className="rz-module-pad">
          {/* Le ciblage CSS passe par la classe, pas par l'id : au montage,
              Planity réécrit l'attribut id de ce conteneur en
              "planity-appointment" (le nœud React lui-même est conservé). */}
          <div className="rz-module" id="planity-widget-container" ref={widgetContainerRef}>
            {bookingStatus === 'unavailable' ? (
              <PlanityWidgetUnavailable planityUrl={booking.planityPublicUrl} />
            ) : (
              <PlanityWidgetLoading />
            )}
          </div>
        </div>
      </section>

      <p className='rz-gift'>
        Vous souhaitez offrir ?{' '}
        <a href='/offrir' onClick={() => giftCardOpened('reserver_lien')}>
          Découvrir nos cartes cadeaux →
        </a>
      </p>

    </div>
  );
}

export default ReserverEnLigne;
